"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// --- Tooltip data (optional) ---
type Platform = "youtube" | "tiktok" | "instagram" | "traktrain"
type UploadItem = { id: string; title: string; platform: Platform; status: "scheduled" | "published" }
export type ItemsByDate = Record<string, UploadItem[]>

function dateKey(d: Date): string {
  // local YYYY-MM-DD (avoid UTC shift)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function PlatformIcon({ platform, className }: { platform: Platform; className?: string }) {
  switch (platform) {
    case "youtube":
      return (
        <svg viewBox="0 0 24 24" aria-label="YouTube" className={cn("h-3.5 w-3.5", className)}>
          <path d="M23 7.5s-.2-1.7-.9-2.5c-.9-.9-1.9-.9-2.4-1C16.8 3.6 12 3.6 12 3.6h0s-4.8 0-7.7.4c-.5.1-1.5.1-2.4 1C.2 5.8 0 7.5 0 7.5S-.2 9.4 0 11.3c.2 1.8.9 2.5.9 2.5.9.9 2.1.9 2.7 1 2 .2 8.4.4 8.4.4s4.8 0 7.7-.4c.5-.1 1.5-.1 2.4-1 .7-.8.9-2.5.9-2.5s.2-1.9 0-3.8zM9.6 10.8V6.8l4.6 2-4.6 2z" fill="currentColor"/>
        </svg>
      )
    case "tiktok":
      return (
        <svg viewBox="0 0 24 24" aria-label="TikTok" className={cn("h-3.5 w-3.5", className)}>
          <path d="M14.5 3c.6 1.8 2.1 3.2 4 3.7v3.2c-1.6 0-3.2-.5-4.5-1.4v6.3a5.8 5.8 0 11-4.8-5.7v3.3a2.6 2.6 0 102 2.6V3h3.3z" fill="currentColor"/>
        </svg>
      )
    case "instagram":
      return (
        <svg viewBox="0 0 24 24" aria-label="Instagram" className={cn("h-3.5 w-3.5", className)}>
          <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5A5.5 5.5 0 1111.999 19 5.5 5.5 0 0112 7.5zm0 2A3.5 3.5 0 1015.5 13 3.5 3.5 0 0012 9.5zM18 6.3a1 1 0 110 2 1 1 0 010-2z" fill="currentColor"/>
        </svg>
      )
    case "traktrain":
      return (
        <div aria-label="Traktrain" className={cn("h-3.5 w-3.5 grid place-items-center text-[10px] font-semibold", className)}>
          TT
        </div>
      )
  }
}

interface UploadDateTimePickerProps {
  scheduledDates: Date[]
  publishedDates: Date[]
  selectedDateTime?: string
  onDateTimeChange?: (datetime: string) => void
  label?: string
  /** Optional: tooltip data keyed by local YYYY-MM-DD */
  itemsByDate?: ItemsByDate
}

function UploadDateTimePicker({
  scheduledDates,
  publishedDates,
  selectedDateTime,
  onDateTimeChange,
  label = "Schedule Date & Time",
  itemsByDate,
}: UploadDateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [currentMonth, setCurrentMonth] = React.useState(new Date())
  
  const selectedDate = selectedDateTime ? new Date(selectedDateTime) : null
  const selectedTime = selectedDateTime 
    ? new Date(selectedDateTime).toTimeString().slice(0, 8) 
    : "10:30:00"

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    const days: (Date | null)[] = []
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null)
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day))
    return days
  }

  const handleDateSelect = (date: Date) => {
    if (date && onDateTimeChange) {
      const [hours, minutes, seconds] = selectedTime.split(':').map(Number)
      const newDateTime = new Date(date)
      newDateTime.setHours(hours, minutes, seconds)
      onDateTimeChange(newDateTime.toISOString())
    }
    setOpen(false)
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedDate && onDateTimeChange) {
      const [hours, minutes, seconds] = e.target.value.split(':').map(Number)
      const newDateTime = new Date(selectedDate)
      newDateTime.setHours(hours, minutes, seconds || 0)
      onDateTimeChange(newDateTime.toISOString())
    }
  }

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString()
  const isSelected = (date: Date) => !!selectedDate && date.toDateString() === selectedDate.toDateString()

  const isPastDate = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const isScheduled = (date: Date) =>
    scheduledDates.some((d) => d.toDateString() === date.toDateString())
  const isPublished = (date: Date) =>
    publishedDates.some((d) => d.toDateString() === date.toDateString())

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ]
  const weekdays = ["Su","Mo","Tu","We","Th","Fr","Sa"]

  const days = getDaysInMonth(currentMonth)

  return (
    <div className="space-y-4 ">
      <div className="flex gap-4">
        <div className="flex flex-col gap-3">
          <Label className="px-1">Date</Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-40 justify-between font-normal">
                {selectedDate ? selectedDate.toLocaleDateString() : "Select date"}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start">
              <div className="space-y-4">
                {/* Month/Year Header */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="font-medium">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Calendar Grid with tooltip previews */}
                <TooltipProvider delayDuration={1500}>
                  <div className="grid surface grid-cols-7 gap-1">
                    {/* Weekday Headers */}
                    {weekdays.map((day) => (
                      <div key={day} className="text-center text-xs font-medium text-muted-foreground p-2">
                        {day}
                      </div>
                    ))}

                    {/* Calendar Days */}
                    {days.map((date, index) => {
                      if (!date) return <div key={index} className="h-9 w-9" />

                      const scheduled = isScheduled(date)
                      const published = isPublished(date)
                      const items = itemsByDate?.[dateKey(date)] ?? []
                      const disabled = isPastDate(date)

                      const ButtonEl = (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-9 w-9 p-0 font-normal relative",
                            isSelected(date) && "bg-primary text-primary-foreground",
                            isToday(date) && !isSelected(date) && "bg-accent text-accent-foreground",
                            disabled ? "text-muted-foreground opacity-50 cursor-not-allowed" : "hover:bg-accent hover:text-accent-foreground",
                            published && "ring-2 ring-green-500 ring-offset-2 ring-offset-background",
                            scheduled && !published && "ring-2 ring-blue-500 ring-offset-2 ring-offset-background"
                          )}
                          onClick={() => !disabled && handleDateSelect(date)}
                          disabled={disabled}
                        >
                          {date.getDate()}
                        </Button>
                      )

                      // Wrap the (possibly disabled) button so tooltip still triggers on hover
                      const Trigger = <span className="inline-block">{ButtonEl}</span>

                      return items.length > 0 ? (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>{Trigger}</TooltipTrigger>
                          <TooltipContent side="top" align="center" className="max-w-64 p-2">
                            <div className="space-y-1.5">
                              {items.map((item) => (
                                <div key={item.id} className="flex items-center gap-2 truncate">
                                  <PlatformIcon platform={item.platform} />
                                  <span className="truncate text-xs font-medium leading-4" title={item.title}>
                                    {item.title}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <div key={index} className="text-center">{Trigger}</div>
                      )
                    })}
                  </div>
                </TooltipProvider>

                {/* Legend inside calendar */}
                <div className="flex items-center justify-center gap-4 pt-3 border-t text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-green-500 rounded-sm" />
                    <span>Published</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-blue-500 rounded-sm" />
                    <span>Scheduled</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col gap-3">
          <Label htmlFor="time-picker" className="px-1">Time</Label>
          <Input
            type="time"
            id="time-picker"
            step="1"
            value={selectedTime}
            onChange={handleTimeChange}
            className="w-32"
          />
        </div>
      </div>
    </div>
  )
}

UploadDateTimePicker.displayName = "UploadDateTimePicker"
export { UploadDateTimePicker }
